import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { CommonModule } from '@angular/common';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import {
  APP_ROUTE_PREFIX,
  AppModule,
  authorityIds,
  clients,
  CURRENT_INSURANCE_MODE,
  ETF_ROUTE_PREFIX,
  getSidebarForModule,
  operations,
  OSMA_ROUTE_PREFIX,
  OSMAPages,
  subAuthorities,
} from '@app/enums';
import { NzInputModule } from 'ng-zorro-antd/input';
import { FormsModule } from '@angular/forms';
import { debounce, DebouncedFunc } from 'lodash';
import { RequestService } from '@app/_services/request.service';
import { NotificationService } from '@app/_services/_common/notification.service';
import { LoaderComponent } from '@app/common/loader/loader.component';
import { NzListModule } from 'ng-zorro-antd/list';
import { UtilityService } from '@app/_services/utility.service';
import { PermissionService } from '@app/_services/permission.service';
import { SharedUserMenuComponent } from '../shared-user-menu/shared-user-menu.component';
import { filter } from 'rxjs';

interface SearchItem {
  id_type: string; // e.g., "portfolio_id"
  id_value: number; // e.g., 3
  index?: number; // e.g., 0
  item: string; // e.g., "portfolio"
  search_string: string; // e.g., "SVH|Smart Veta Hedged"
}
@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    NzIconModule,
    NzDropdownModule,
    NzButtonModule,
    NzMenuModule,
    RouterLink,
    NzFlexModule,
    NzInputModule,
    FormsModule,
    CommonModule,
    LoaderComponent,
    NzListModule,
    SharedUserMenuComponent,
  ],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
  @Input() isCollapsed: boolean = false;
  @Input() isHomeHeader: boolean = false;
  @Input() isInsuranceActive: boolean = false;
  @Output() toggle = new EventEmitter<void>();
  userTypeList = [
    {
      label: 'ALSC',
      value: clients.alsc,
    },
    {
      label: 'CLIC',
      value: clients.clic,
    },
  ];
  currentMode = clients.alsc;
  private debouncedSearch: DebouncedFunc<(query: string) => void>;

  private router = inject(Router);

  visible = false;
  sideBarlist: any = [];
  moduleNameWithColor: { label: string; color: string } = { label: '', color: '' };
  search: string = '';
  searchLoader: boolean = false;
  showSuggestions: boolean = false;
  searchResults: any[] = [];
  homeRoute = APP_ROUTE_PREFIX;
  isSearchVisible = true;
  loader: boolean = false;
  isProfilePage = false;

  constructor(
    private REQ: RequestService,
    private notification: NotificationService,
    private utilityService: UtilityService,
    private permService: PermissionService
  ) {
    this.debouncedSearch = debounce(this.performSearch, 500);
    let currentModule = this.utilityService.getModuleName();
    let sidebarList = getSidebarForModule(currentModule);

    if (!this.router.url.includes('app/home')) {
      this.moduleNameWithColor = this.getModuleName(currentModule);
    }

    this.sideBarlist = sidebarList.filter((item: any) => {
      if (item.pathName === OSMAPages.ALERTS || item.pathName === OSMAPages.ACCOUNT_INTAKE) {
        return item;
      }
      if (this.visibilityStatus(currentModule, item.pathName)) {
        return item;
      }
    });

    this.isSearchVisible = this.visibilityStatus(currentModule, 'search');
    this.checkProfilePage();

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.checkProfilePage();
    });
  }
  private checkProfilePage(): void {
    this.isProfilePage = this.router.url.split('?')[0] === '/app/home/profile';
  }
  ngOnInit() {
    const currentMode = this.utilityService.currentInsuranceMode;
    if (typeof currentMode === 'number') {
      this.currentMode = currentMode;
    } else {
      this.utilityService.setLocalStorage(CURRENT_INSURANCE_MODE, this.currentMode);
    }
  }

  getModuleName(currentModule: string): { label: string; color: string } {
    const modules: { [key: string]: { label: string; color: string } } = {
      [AppModule.ETF]: { label: 'ETF', color: '#5856d6' },
      [AppModule.INSURANCE]: { label: 'Insurance', color: '#1890ff' },
      [AppModule.OCIO]: { label: 'OCIO', color: '#F9B115' },
      [AppModule.OSMA]: { label: 'Options SMA', color: '#E55353' },
      [AppModule.STRUCTURED_PRODUCTS]: { label: 'Structured Products', color: '#52c41a' },
    };
    return modules[currentModule] || '';
  }

  handleToggle() {
    this.toggle.emit();
  }

  isActive(path: string): boolean {
    // forward slash / is to fix the issue with advisor getting highlighted when on advisor-dashboard.
    const url = `${this.router.url}/`;
    return url.includes(`${path}/`);
  }

  closeMenuPopup() {
    this.visible = false;
  }

  onInputChange(value: string): void {
    if (!value || value.trim() === '') {
      this.searchResults = [];
      this.searchLoader = false;
      this.showSuggestions = false;
      return;
    }
    this.showSuggestions = true;
    this.debouncedSearch(value);
  }

  onInputFocus(): void {
    if (this.search && this.search.trim() !== '') {
      this.showSuggestions = true;
    }
  }

  performSearch = (query: string) => {
    this.searchLoader = true;
    let qry = {
      params: {
        search_me: query,
      },
      user_data: {
        authority: authorityIds.SEARCH,
        sub_authority: subAuthorities.GENERAL.SEARCH,
        operation: operations.SEARCH,
      },
    };

    this.REQ.get('search/list', { query: JSON.stringify(qry) }).subscribe(
      (response: any) => {
        this.searchLoader = false;
        if (response.status === 'Failed') {
          this.notification.error(response.message);
          this.searchResults = [];
          return;
        }
        this.searchResults = response.data || [];
      },
      (error) => {
        this.searchLoader = false;
        this.searchResults = [];
        this.notification.error(error.message);
      }
    );
  };

  selectSuggestion(data: SearchItem): void {
    const path = {
      portfolio: `${ETF_ROUTE_PREFIX}/portfolio/details`,
      instrument: `${ETF_ROUTE_PREFIX}/instrument/details`,
      client: `${OSMA_ROUTE_PREFIX}/clients/details`,
      account: `${OSMA_ROUTE_PREFIX}/account/details`,
    } as const;
    this.search = data?.search_string || '';
    if (data.item in path) {
      this.router.navigate([path[data.item as keyof typeof path], data.id_value]);
    }

    this.showSuggestions = false;
  }

  ngOnDestroy(): void {
    this.showSuggestions = false;
    this.debouncedSearch.cancel();
  }

  // Optional: hide suggestions on outside click
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.search-container')) {
      this.showSuggestions = false;
    }
  }

  visibilityStatus(moduleName: string, pageName: string) {
    return this.permService.hasPageAccess(moduleName, pageName);
  }

  onSelectionChange(val: any) {
    this.currentMode = val;
    this.utilityService.setLocalStorage(CURRENT_INSURANCE_MODE, val);
    window.location.reload();
  }

  get user() {
    return this.userTypeList.find((item) => item.value === this.currentMode)?.label.toUpperCase();
  }
}
